import type { JSONContent } from '@tiptap/core';
import {
  parseBulletinBodySegments,
  parseFormattedRuns,
  type FormattedRun,
} from './bulletinBodySegments';

type MarkLike = { type: string };

/** FormattedRun を葉のテキストノード列にし、親から受け取ったマークを付与する */
function formattedRunsToTextNodes(runs: FormattedRun[], parentMarks: MarkLike[]): JSONContent[] {
  const out: JSONContent[] = [];
  for (const r of runs) {
    if (r.kind === 'plain') {
      if (r.text) {
        out.push({
          type: 'text',
          text: r.text,
          marks: parentMarks.length ? parentMarks.map((m) => ({ ...m })) : undefined,
        });
      }
    } else {
      const m: MarkLike =
        r.kind === 'bold'
          ? { type: 'bold' }
          : r.kind === 'red'
            ? { type: 'bulletinRed' }
            : r.kind === 'blue'
              ? { type: 'bulletinBlue' }
              : { type: 'bulletinBlack' };
      out.push(...formattedRunsToTextNodes(r.children, [...parentMarks, m]));
    }
  }
  return out;
}

function lineToParagraph(line: string): JSONContent {
  const segments = parseBulletinBodySegments(line);
  if (segments.length === 0) {
    return { type: 'paragraph' };
  }
  const content: JSONContent[] = [];
  for (const seg of segments) {
    if (seg.type === 'mention') {
      content.push({
        type: 'mention',
        attrs: { id: String(seg.staffId), label: seg.displayName },
      });
    } else {
      content.push(...formattedRunsToTextNodes(parseFormattedRuns(seg.text), []));
    }
  }
  return { type: 'paragraph', content: content.length ? content : undefined };
}

/** 掲示板 API 文字列 → TipTap JSON（WYSIWYG 用） */
export function bulletinStringToTipTapJSON(body: string): JSONContent {
  const lines = body.split('\n');
  return {
    type: 'doc',
    content: lines.map(lineToParagraph),
  };
}

function wrapTextWithMarks(text: string, marks: MarkLike[]): string {
  if (!marks.length) return text;
  const hasBold = marks.some((m) => m.type === 'bold');
  const hasRed = marks.some((m) => m.type === 'bulletinRed');
  const hasBlue = marks.some((m) => m.type === 'bulletinBlue');
  const hasBlack = marks.some((m) => m.type === 'bulletinBlack');
  let open = '';
  let close = '';
  if (hasRed) {
    open += '{{red}}';
    close = '{{/red}}' + close;
  }
  if (hasBlue) {
    open += '{{blue}}';
    close = '{{/blue}}' + close;
  }
  if (hasBlack) {
    open += '{{black}}';
    close = '{{/black}}' + close;
  }
  if (hasBold) {
    open += '**';
    close = '**' + close;
  }
  return open + text + close;
}

function serializeTextNode(node: JSONContent): string {
  const text = node.text ?? '';
  const marks = (node.marks ?? []) as MarkLike[];
  return wrapTextWithMarks(text, marks);
}

function paragraphToBulletinString(p: JSONContent): string {
  if (!p.content?.length) return '';
  let s = '';
  for (const node of p.content) {
    if (node.type === 'text') {
      s += serializeTextNode(node);
    } else if (node.type === 'mention') {
      const id = node.attrs?.id;
      const rawLabel = node.attrs?.label != null ? String(node.attrs.label) : '';
      const label = rawLabel.replace(/\]/g, '');
      s += `@[${label}](${id})`;
    }
  }
  return s;
}

/** TipTap JSON → 掲示板 API 文字列 */
export function tipTapJSONToBulletinString(doc: JSONContent): string {
  if (!doc.content?.length) return '';
  return doc.content.map((p) => paragraphToBulletinString(p)).join('\n');
}
